import { creditTransactionSortSchema } from "../../../service/credit-transaction-sort.models";
import { z } from "zod";

import {
  stripeCatalogSubscriptionTierSchema,
  stripeDiscountCodeSchema,
  stripeStandardSubscriptionTierSchema,
} from "./super-admin-transport";
import {
  AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS,
  LEGACY_AUTO_TOP_UP_CONSENT_VERSION,
  LEGACY_AUTO_TOP_UP_MINIMUM_MONTHLY_LIMIT_CENTS,
} from "../stripe-types";

/**
 * User-facing billing-v2 input schemas. Unlike super-admin counterparts, none
 * of these carry `teamId` — the team is always derived from `ctx.auth.teamId`
 * server-side so a member can only operate on their own team's billing.
 *
 * Admin operations (cross-team views, manual overrides, custom plans, etc.)
 * keep using the superAdminBilling* schemas from super-admin-transport.ts via
 * the nested `admin` sub-router.
 */

const billingV2StripeCustomerContextParams = z.object({
  customerEmail: z.string().email().optional(),
  teamName: z.string().min(1).optional(),
  teamSlug: z.string().min(1).optional(),
});

const billingV2StripeUrlParams = z.object({
  successUrl: z.string().url().max(2048),
  cancelUrl: z.string().url().max(2048),
});

export const billingV2CreateSubscriptionCheckoutSessionParams = z
  .object({
    targetTier: stripeCatalogSubscriptionTierSchema,
    amountCents: z.number().int().min(1_000).max(1_000_000).optional(),
  })
  .merge(billingV2StripeUrlParams)
  .merge(billingV2StripeCustomerContextParams);

export type BillingV2CreateSubscriptionCheckoutSessionParams = z.infer<
  typeof billingV2CreateSubscriptionCheckoutSessionParams
>;

export const fastWebSubscriptionPlanSchema = z.enum(["operator", "studio"]);

export const billingV2CreateCommercialSubscriptionCheckoutSessionParams = z
  .object({
    targetPlan: fastWebSubscriptionPlanSchema,
  })
  .merge(billingV2StripeUrlParams)
  .merge(billingV2StripeCustomerContextParams);

export const billingV2ScheduleSubscriptionPlanChangeParams = z.object({
  targetPlan: fastWebSubscriptionPlanSchema,
});

export const billingV2SetSubscriptionCancelAtPeriodEndParams = z.object({
  cancelAtPeriodEnd: z.boolean(),
});

/**
 * Finalize an onboarding Checkout Session on browser return. The server reads
 * the completed session from Stripe, resolves the captured payment method
 * (setup intent for the free plan, subscription for paid plans), and promotes
 * it to the team default. Only the Checkout Session id is accepted.
 */
export const billingV2CompleteCheckoutSetupParams = z.object({
  checkoutSessionId: z.string().min(1).max(255),
});

export type BillingV2CompleteCheckoutSetupParams = z.infer<
  typeof billingV2CompleteCheckoutSetupParams
>;

/**
 * Start the card-required $0 free-plan checkout. This is the post-onboarding
 * card-collection vehicle: completing it puts the team on the free Stripe
 * subscription, captures a default payment method, and grants the one-time
 * first-payment-method credit. The target tier is always free, so no tier is
 * accepted from the client.
 */
export const billingV2CreateFreePlanCheckoutSessionParams = z
  .object({})
  .merge(billingV2StripeUrlParams)
  .merge(billingV2StripeCustomerContextParams);

export type BillingV2CreateFreePlanCheckoutSessionParams = z.infer<
  typeof billingV2CreateFreePlanCheckoutSessionParams
>;

export const billingV2CreateCreditCheckoutSessionParams = z
  .object({
    amountCents: z.number().int().min(100).max(1_000_000),
    discountCode: stripeDiscountCodeSchema,
  })
  .merge(billingV2StripeUrlParams)
  .merge(billingV2StripeCustomerContextParams);

export type BillingV2CreateCreditCheckoutSessionParams = z.infer<
  typeof billingV2CreateCreditCheckoutSessionParams
>;

const billingV2OnboardingContinuityParams = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("pause_when_empty") }),
  z.object({
    kind: z.literal("keep_running"),
    thresholdNanocredits: z.string().regex(/^\d+$/),
    amountCents: z.number().int().min(1_000).max(1_000_000),
    monthlyLimitCents: z
      .number()
      .int()
      .min(1_000)
      .max(AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS),
    consentVersion: z.literal("fast_web_auto_top_up_v1"),
  }),
]);

/** Unified fast-web funding Checkout: one paid path plus explicit continuity. */
export const billingV2CreateOnboardingBillingCheckoutParams = z
  .object({
    funding: z.discriminatedUnion("kind", [
      z.object({
        kind: z.literal("payg"),
        amountCents: z.number().int().min(1_000).max(1_000_000),
      }),
      z.object({
        kind: z.literal("subscription"),
        planKey: fastWebSubscriptionPlanSchema,
      }),
    ]),
    continuity: billingV2OnboardingContinuityParams,
  })
  .merge(billingV2StripeUrlParams)
  .merge(billingV2StripeCustomerContextParams);

export type BillingV2CreateOnboardingBillingCheckoutParams = z.infer<
  typeof billingV2CreateOnboardingBillingCheckoutParams
>;

export const billingV2CreatePortalSessionParams = z
  .object({
    returnUrl: z.string().url().max(2048),
  })
  .merge(billingV2StripeCustomerContextParams);

export type BillingV2CreatePortalSessionParams = z.infer<
  typeof billingV2CreatePortalSessionParams
>;

export const billingV2CreateSubscriptionPortalSessionParams = z
  .object({
    returnUrl: z.string().url().max(2048),
    flow: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("cancel") }),
      z.object({
        kind: z.literal("update"),
        targetTier: stripeStandardSubscriptionTierSchema,
      }),
    ]),
  })
  .merge(billingV2StripeCustomerContextParams);

export type BillingV2CreateSubscriptionPortalSessionParams = z.infer<
  typeof billingV2CreateSubscriptionPortalSessionParams
>;

export const billingV2UpdateAutoTopUpSettingsParams = z.object({
  enabled: z.boolean(),
  amountCents: z.number().int().min(1_000).max(1_000_000).nullable(),
  monthlyLimitCents: z
    .number()
    .int()
    .min(1_000)
    .max(AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS)
    .nullable()
    .optional(),
  consentVersion: z.string().min(1).max(100).nullable().optional(),
  thresholdNanocredits: z
    .string()
    .regex(/^-?\d+$/)
    .nullable(),
});

export type BillingV2UpdateAutoTopUpSettingsParams = z.infer<
  typeof billingV2UpdateAutoTopUpSettingsParams
>;

/**
 * Older dashboard clients did not send explicit cap consent. Preserve their
 * existing settings by recording the historical default only at the transport
 * boundary; new clients always provide both values explicitly.
 */
export function resolveAutoTopUpTransportConfiguration({
  amountCents,
  consentVersion,
  enabled,
  monthlyLimitCents,
}: {
  amountCents: number | null;
  consentVersion?: string | null;
  enabled: boolean;
  monthlyLimitCents?: number | null;
}): { consentVersion: string | null; monthlyLimitCents: number | null } {
  if (!enabled) {
    return { consentVersion: null, monthlyLimitCents: null };
  }

  return {
    consentVersion: consentVersion ?? LEGACY_AUTO_TOP_UP_CONSENT_VERSION,
    monthlyLimitCents:
      monthlyLimitCents ??
      Math.max(
        (amountCents ?? 0) * 5,
        LEGACY_AUTO_TOP_UP_MINIMUM_MONTHLY_LIMIT_CENTS,
      ),
  };
}

export const billingV2RetryInvoicePaymentParams = z.object({
  invoiceId: z.string().min(1),
});

export type BillingV2RetryInvoicePaymentParams = z.infer<
  typeof billingV2RetryInvoicePaymentParams
>;

export const billingV2ExportInvoiceDetailParams = z.object({
  invoiceId: z.string().uuid(),
  format: z.enum(["xlsx", "csv"]).default("xlsx"),
});

export type BillingV2ExportInvoiceDetailParams = z.infer<
  typeof billingV2ExportInvoiceDetailParams
>;

export const billingV2CreditTransactionDirectionSchema = z
  .enum(["all", "credits", "debits"])
  .default("all");

export const billingV2ListCreditTransactionsParams = z.object({
  direction: billingV2CreditTransactionDirectionSchema,
  limit: z.number().int().min(1).max(100).default(50),
  offset: z.number().int().min(0).default(0),
  /** Exact ledger reasons to include (e.g. top-up reasons or `*_overage`). */
  reasons: z.array(z.string().min(1)).max(64).optional(),
  sort: creditTransactionSortSchema.nullish(),
});

export type BillingV2ListCreditTransactionsParams = z.infer<
  typeof billingV2ListCreditTransactionsParams
>;

export const billingV2CreditTransactionReceiptParams = z.object({
  creditTransactionId: z.string().min(1),
});

/**
 * Window + resolution for the usage page's spend-over-time series. The window
 * is relative (`sinceHoursAgo` back from now) so clients need no date wire
 * format; a year is plenty for every preset range plus its comparison window.
 */
export const billingV2UsageSpendSeriesParams = z.object({
  sinceHoursAgo: z
    .number()
    .int()
    .min(1)
    .max(24 * 366),
  bucket: z.enum(["hour", "day"]),
});

export type BillingV2UsageSpendSeriesParams = z.infer<
  typeof billingV2UsageSpendSeriesParams
>;

export type BillingV2CreditTransactionReceiptParams = z.infer<
  typeof billingV2CreditTransactionReceiptParams
>;

export const billingV2PaymentMethodStatusParams = z.object({
  /**
   * Skip the cached value and read the team's payment-method status from
   * Stripe. The forced read is rate-limited server-side (at most once per
   * minute per team), so setting this on every poll is safe.
   */
  forceRefresh: z.boolean().optional(),
});

export type BillingV2PaymentMethodStatusParams = z.infer<
  typeof billingV2PaymentMethodStatusParams
>;

/**
 * Create a SetupIntent to add a team's default payment method in-app. Only
 * carries optional Stripe customer context — the team is derived from auth.
 */
export const billingV2CreateDefaultPaymentMethodSetupIntentParams =
  billingV2StripeCustomerContextParams;

export type BillingV2CreateDefaultPaymentMethodSetupIntentParams = z.infer<
  typeof billingV2CreateDefaultPaymentMethodSetupIntentParams
>;

/**
 * Finalize the in-app setup flow after Stripe.js confirms the SetupIntent. The
 * server re-reads the intent from Stripe and never trusts a client-supplied
 * payment method, so only the SetupIntent id is accepted here.
 */
export const billingV2CompleteDefaultPaymentMethodSetupParams = z.object({
  setupIntentId: z.string().min(1).max(255),
});

export type BillingV2CompleteDefaultPaymentMethodSetupParams = z.infer<
  typeof billingV2CompleteDefaultPaymentMethodSetupParams
>;

/**
 * Stages of the in-app default payment method flow where a card decline or
 * setup failure can surface. Bounds on the string fields are deliberate —
 * these values reach logs and metrics labels.
 */
export const PAYMENT_METHOD_FAILURE_STAGES = ["confirm", "complete"] as const;
export type PaymentMethodFailureStage =
  (typeof PAYMENT_METHOD_FAILURE_STAGES)[number];

export const billingV2RecordPaymentMethodFailureParams = z.object({
  stage: z.enum(PAYMENT_METHOD_FAILURE_STAGES),
  code: z.string().min(1).max(64),
  declineCode: z.string().min(1).max(64).optional(),
  setupIntentId: z.string().min(1).max(64).optional(),
});
export type BillingV2RecordPaymentMethodFailureParams = z.infer<
  typeof billingV2RecordPaymentMethodFailureParams
>;
